import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { Play, Loader2, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import PtfButton from "@/components/PtfButton";
import Countdown from "@/components/Countdown";
import { Reveal } from "@/components/motion/Reveal";
import { api } from "@/lib/api";
import { trackMetaEvent } from "@/lib/meta";
import { FILM_STATUS, FESTIVAL_STATUSES } from "@/lib/expeditions";

function youtubeEmbedUrl(url) {
  if (!url) return "";
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : url;
}

export const FilmCard = ({ f }) => (
  <Link to={`/films/${f.slug}`} data-testid={`film-card-${f.slug}`} className="group block bg-white border border-ptf-primary/12 hover:border-ptf-primary/40 transition-colors overflow-hidden">
    <div className="aspect-video overflow-hidden bg-ptf-primary relative">
      {f.image ? (
        <img src={f.image} alt={f.title} className="h-full w-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-700" loading="lazy" />
      ) : (
        <div className="absolute inset-0 grid place-items-center text-ptf-bg/40 font-display uppercase tracking-tight text-2xl">Coming Soon</div>
      )}
      <span className="absolute bottom-3 left-3 grid place-items-center h-10 w-10 rounded-full border border-white/60 text-white"><Play className="h-4 w-4 ml-0.5" /></span>
      <span className="absolute top-3 right-3 text-[0.58rem] font-semibold uppercase tracking-[0.14em] bg-ptf-bg/90 text-ptf-primary px-2.5 py-1">{FILM_STATUS[f.status] || f.status}</span>
    </div>
    <div className="p-6">
      <p className="text-[0.6rem] uppercase tracking-[0.2em] text-ptf-text/50">{f.category}{f.year ? ` · ${f.year}` : ""}</p>
      <h3 className="mt-2 font-display uppercase text-2xl tracking-tight text-ptf-primary leading-tight group-hover:text-ptf-secondary transition-colors">{f.title}</h3>
      {f.tagline && <p className="mt-1 font-serif-ed italic text-ptf-secondary text-sm">{f.tagline}</p>}
      <p className="mt-2 text-[0.62rem] uppercase tracking-[0.14em] text-ptf-text/55">{[f.location, f.duration, f.expedition_title].filter(Boolean).join(" · ")}</p>
      <span className="mt-4 inline-flex items-center gap-1 text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-ptf-primary group-hover:text-ptf-secondary transition-colors">
        {f.video_url && f.status === "RELEASED" ? "Watch Film" : "Coming Soon · View"} <ArrowUpRight className="h-3.5 w-3.5" />
      </span>
    </div>
  </Link>
);

export default function Films() {
  const { data: films = [], isLoading } = useQuery({ queryKey: ["films"], queryFn: async () => (await api.get("/films")).data });
  return (
    <>
      <Seo title="Films: The Planet Trotter Film Archive" path="/films" description="Documentaries, expedition films and field stories from Planet Trotter journeys across mountains, rivers, oceans, land and wildlife. Dawn: A Project of Belief, The Last Horizon and High Altitude are in pre-production." />
      <PageHero
        eyebrow="Expeditions & Films · The archive"
        titleLines={["From the field.", "To the screen."]}
        intro="Every Planet Trotter film grows out of an expedition. This archive shows each film with its honest production status; nothing is presented as released, selected or awarded unless it is."
        image="/dawn/vor-dusk.jpg"
      />
      <section className="bg-ptf-bg py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          {isLoading && <Loader2 className="h-6 w-6 animate-spin text-ptf-primary" />}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="films-grid">
            {films.map((f) => <FilmCard key={f.slug} f={f} />)}
          </div>
          <div className="mt-12"><PtfButton to="/expeditions" data-testid="films-expeditions-cta">Explore the Expeditions</PtfButton></div>
        </div>
      </section>
    </>
  );
}

export function FilmDetail() {
  const { slug } = useParams();
  const { data: f, isLoading } = useQuery({ queryKey: ["film", slug], queryFn: async () => (await api.get(`/films/${slug}`)).data });
  useEffect(() => {
    if (f) trackMetaEvent("ViewContent", { content_name: f.title, content_type: "film", content_ids: [f.slug] });
  }, [f]);
  if (isLoading) return <div className="min-h-[60vh] grid place-items-center bg-ptf-bg"><Loader2 className="h-6 w-6 animate-spin text-ptf-primary" /></div>;
  if (!f) return null;
  const jsonLd = f.video_url ? [{ "@context": "https://schema.org", "@type": "VideoObject", name: f.title, description: f.synopsis, uploadDate: f.created_at }] : undefined;
  return (
    <>
      <Seo title={`${f.title} | Film`} path={`/films/${f.slug}`} description={f.synopsis} image={f.image} jsonLd={jsonLd} />
      <section className="relative bg-ptf-primary text-ptf-bg grain overflow-hidden">
        {f.image && <img src={f.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />}
        <div className="absolute inset-0 bg-gradient-to-t from-ptf-primary via-ptf-primary/60 to-ptf-primary/30" />
        <div className="relative mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 pt-40 pb-16">
          <p className="text-[0.68rem] uppercase tracking-[0.24em] text-ptf-accent">{f.category}{f.year ? ` · ${f.year}` : ""} · {FILM_STATUS[f.status] || f.status}</p>
          <h1 data-testid="film-title" className="mt-3 font-display font-extrabold uppercase text-4xl sm:text-6xl leading-[0.92] tracking-tight max-w-4xl">{f.title}</h1>
          {f.tagline && <p className="mt-4 font-serif-ed italic text-xl text-ptf-bg/85">{f.tagline}</p>}
          <p className="mt-3 text-[0.65rem] uppercase tracking-[0.14em] text-ptf-bg/65">{[f.location, f.duration, f.expedition_title].filter(Boolean).join(" · ")}</p>
        </div>
      </section>
      <section className="bg-ptf-bg py-14 md:py-20">
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12">
          {f.countdown_date && (
            <div className="mb-10 max-w-sm">
              <Countdown targetDate={f.countdown_date} label={`${f.title.split(":")[0]} journey begins in`} />
            </div>
          )}
          {f.video_url ? (
            <div className="aspect-video border border-ptf-primary/12 bg-ptf-primary mb-10">
              <iframe data-testid="film-player" src={youtubeEmbedUrl(f.video_url)} title={f.title} className="h-full w-full" allowFullScreen loading="lazy" />
            </div>
          ) : (
            <div data-testid="film-coming-soon" className="border border-ptf-primary/15 bg-white px-5 py-4 text-sm text-ptf-text/70 mb-10">
              This film is in {(FILM_STATUS[f.status] || f.status).toLowerCase()}. It will premiere here when released.
            </div>
          )}
          {f.video_gallery?.length > 0 && (
            <div className="mb-14" data-testid="film-video-gallery">
              <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-4">More from the field</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {f.video_gallery.map((v, i) => (
                  <div key={v.url || i} data-testid={`film-gallery-video-${i}`}>
                    <div className="aspect-video border border-ptf-primary/12 bg-ptf-primary overflow-hidden">
                      <iframe src={youtubeEmbedUrl(v.url)} title={v.caption || `${f.title} clip ${i + 1}`} className="h-full w-full" allowFullScreen loading="lazy" />
                    </div>
                    {v.caption && <p className="mt-2.5 text-sm text-ptf-text/70 leading-snug">{v.caption}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-0">
            {[["Synopsis", f.synopsis], ["The story", f.story], ["Behind the journey", f.behind]].filter(([, v]) => v).map(([t, v]) => (
              <Reveal key={t}>
                <div className="py-6 border-t border-ptf-primary/12 grid grid-cols-1 md:grid-cols-12 gap-3">
                  <h2 className="md:col-span-4 font-display uppercase text-xl tracking-tight text-ptf-primary">{t}</h2>
                  <p className="md:col-span-8 text-sm text-ptf-text/75 leading-relaxed whitespace-pre-line">{v}</p>
                </div>
              </Reveal>
            ))}
            {f.filmmakers?.length > 0 && (
              <div className="py-6 border-t border-ptf-primary/12 grid grid-cols-1 md:grid-cols-12 gap-3">
                <h2 className="md:col-span-4 font-display uppercase text-xl tracking-tight text-ptf-primary">Film team</h2>
                <div className="md:col-span-8 flex flex-wrap gap-2">{f.filmmakers.map((m) => <span key={m} className="text-[0.65rem] uppercase tracking-[0.12em] border border-ptf-primary/20 px-3 py-1.5">{m}</span>)}</div>
              </div>
            )}
            <div className="py-6 border-t border-ptf-primary/12 grid grid-cols-1 md:grid-cols-12 gap-3" data-testid="festival-journey">
              <h2 className="md:col-span-4 font-display uppercase text-xl tracking-tight text-ptf-primary">Festival journey</h2>
              <div className="md:col-span-8 text-sm text-ptf-text/75 leading-relaxed">
                {f.festival_entries?.length > 0 ? (
                  <div className="space-y-2">
                    {f.festival_entries.map((e, i) => (
                      <p key={`${e.festival}-${i}`}><strong className="text-ptf-primary">{e.festival}</strong>{e.country ? `, ${e.country}` : ""} · {FESTIVAL_STATUSES.includes(e.status) ? e.status : "SUBMITTED"}{e.category ? ` · ${e.category}` : ""}</p>
                    ))}
                  </div>
                ) : (
                  <p>No festival submissions yet. The film may be submitted to appropriate festivals in India and internationally, subject to editorial completion, eligibility and festival selection.</p>
                )}
              </div>
            </div>
          </div>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            {f.slug === "the-last-horizon-film" && (
              <PtfButton to="/last-horizon/apply" data-testid="film-apply-last-horizon-cta">Apply for The Last Horizon</PtfButton>
            )}
            {f.slug === "dawn-a-project-of-belief" && (
              <PtfButton to="/dawn/apply" data-testid="film-apply-dawn-cta">Apply for DAWN</PtfButton>
            )}
            <PtfButton to="/films" variant={(f.slug === "the-last-horizon-film" || f.slug === "dawn-a-project-of-belief") ? "outline" : "primary"}>Back to the Film Archive</PtfButton>
            <PtfButton to="/expeditions" variant="outline">Explore the Expeditions</PtfButton>
            {f.slug === "the-last-horizon-film" && (
              <PtfButton to="/learning" variant="outline" data-testid="film-learning-training-cta">Learning &amp; Training</PtfButton>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
